import { ArrowBack } from "@mui/icons-material";
import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  CssBaseline,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Topbar from "../../../../components/topbar/TopBar";
import { getCountryByIdUseCase } from "../domain/usecases/getCountryByIdUseCase";
import { updateCountryUseCase } from "../domain/usecases/updateCountryUseCase";

function createInitialForm(country) {
  return {
    id: country?.id || "",
    name: country?.name || "",
    code: country?.code || "",
  };
}

function isSuccessResponse(response) {
  const message = response?.message?.toLowerCase() || "";
  return (
    response?.success === true ||
    message.includes("success") ||
    message.includes("updated")
  );
}

export const CountryUpdatePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form, setForm] = useState(createInitialForm());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  useEffect(() => {
    const loadCountry = async () => {
      try {
        setLoading(true);
        const country = await getCountryByIdUseCase(id);
        setForm(createInitialForm(country));
      } catch (error) {
        setMessage({
          open: true,
          type: "error",
          text: error.message || "No se pudo cargar el pais.",
        });
      } finally {
        setLoading(false);
      }
    };

    loadCountry();
  }, [id]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setForm((prev) => ({
      ...prev,
      [name]: value.toUpperCase(),
    }));
  };

  const handleSave = async () => {
    const payload = {
      id: form.id,
      name: form.name.trim().toUpperCase(),
      code: form.code.trim().toUpperCase(),
    };

    try {
      setSaving(true);
      const response = await updateCountryUseCase(payload);

      if (!isSuccessResponse(response)) {
        throw new Error("No se pudo actualizar el pais.");
      }

      setMessage({
        open: true,
        type: "success",
        text: "Pais actualizado correctamente.",
      });

      window.setTimeout(() => {
        navigate("/admin/geography");
      }, 1200);
    } catch (error) {
      setMessage({
        open: true,
        type: "error",
        text: error.message || "No se pudo actualizar el pais.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="content">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": { color: "#000" },
            p: 1,
          }}
        >
          <Stack spacing={3} alignItems="center">
            <Paper
              elevation={0}
              sx={{
                width: "100%",
                maxWidth: 760,
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 1, md: 2 },
                  py: { xs: 2, md: 2 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      ACTUALIZAR PAIS
                    </Typography>
                    <Typography
                      sx={{ mt: 0.75, color: "#fff !important", opacity: 0.88 }}
                    >
                      Modifica el nombre y codigo del pais en formato mayuscula.
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<ArrowBack />}
                    onClick={() => navigate("/admin/geography")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Volver
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ p: { xs: 1, md: 3 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">Cargando pais...</Typography>
                  </Stack>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      mx: "auto",
                      maxWidth: 480,
                      borderRadius: 3,
                      border: "1px solid rgba(20, 83, 45, 0.12)",
                      p: { xs: 1, md: 2 },
                    }}
                  >
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Nombre"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                      />
                      <TextField
                        fullWidth
                        label="Codigo"
                        name="code"
                        value={form.code}
                        onChange={handleChange}
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{
                          mt: 1,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 700,
                          bgcolor: "#165c53",
                          color: "#fff",
                          "&:hover": {
                            bgcolor: "#12483f",
                          },
                        }}
                      >
                        {saving ? "Actualizando..." : "Actualizar"}
                      </Button>
                    </Stack>
                  </Paper>
                )}
              </Box>
            </Paper>
          </Stack>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
};

export default CountryUpdatePage;
